import dayjs from '~/timeInit';

import type { GridFilterItem, GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';

import type { GHAParticipationSortBy } from '~/services/PublicOfferFeedService';
import type { Dayjs } from '~/services/TimeService';

import {
  COUNTRY_OPTIONS,
  CountryFilterField,
  MAX_PROPERTY_NAME_LENGTH,
  MAX_RANGE_DAYS,
  PROPERTY_NAME_FIELD,
  getCountryFilter,
  getDefaultDateRange,
  getLatestReportDate,
  getPropertyNameFilter,
} from './utils';

export const PAGE_SIZE_OPTIONS = [25, 50, 100];
export const DEFAULT_SORT_MODEL: GridSortModel = [{ field: 'opportunities', sort: 'desc' }];

const SORT_FIELDS: Array<GHAParticipationSortBy> = ['opportunities', 'propertyName', 'userCountry', 'propertyCountry'];
const COUNTRY_PARAMS: Record<CountryFilterField, string> = {
  userCountry: 'userCountries',
  propertyCountry: 'propertyCountries',
};
const PROPERTY_NAME_PARAM = 'propertyName';
const DATE_FORMAT = 'YYYY-MM-DD';
const KNOWN_COUNTRIES = new Set(COUNTRY_OPTIONS.map((option) => option.code));

export type ReportState = {
  dateRange: [Dayjs, Dayjs];
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
  paginationModel: GridPaginationModel;
};

function parseDate(value: string | null): Dayjs | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  const date = dayjs(value);
  return date.isValid() ? date.startOf('day') : null;
}

// Shared links can be stale or hand-edited, so anything the proxy would reject falls back to the default.
export function parseDateRange(params: URLSearchParams): [Dayjs, Dayjs] {
  const start = parseDate(params.get('from'));
  const end = parseDate(params.get('to'));
  if (
    !start ||
    !end ||
    end.isBefore(start) ||
    end.isAfter(getLatestReportDate()) ||
    end.diff(start, 'day') >= MAX_RANGE_DAYS
  ) {
    return getDefaultDateRange();
  }
  return [start, end];
}

function parseCountries(value: string | null) {
  return (value ?? '').split(',').filter((code) => KNOWN_COUNTRIES.has(code));
}

export function parseReportSearchParams(params: URLSearchParams): ReportState {
  const filterItems: Array<GridFilterItem> = (Object.keys(COUNTRY_PARAMS) as Array<CountryFilterField>)
    .map((field) => ({ field, codes: parseCountries(params.get(COUNTRY_PARAMS[field])) }))
    .filter(({ codes }) => codes.length > 0)
    .map(({ field, codes }) => ({ id: field, field, operator: 'isAnyOf', value: codes }));
  const propertyName = params.get(PROPERTY_NAME_PARAM)?.trim().slice(0, MAX_PROPERTY_NAME_LENGTH).trim();
  if (propertyName) {
    filterItems.unshift({
      id: PROPERTY_NAME_FIELD,
      field: PROPERTY_NAME_FIELD,
      operator: 'contains',
      value: propertyName,
    });
  }

  const sortBy = params.get('sortBy') as GHAParticipationSortBy;
  const sortOrder = params.get('sortOrder');
  const pageSize = Number(params.get('pageSize'));
  const page = Number(params.get('page'));

  return {
    dateRange: parseDateRange(params),
    filterModel: { items: filterItems },
    sortModel:
      SORT_FIELDS.includes(sortBy) && (sortOrder === 'asc' || sortOrder === 'desc')
        ? [{ field: sortBy, sort: sortOrder }]
        : DEFAULT_SORT_MODEL,
    paginationModel: {
      page: Number.isInteger(page) && page > 0 ? page : 0,
      pageSize: PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : PAGE_SIZE_OPTIONS[1],
    },
  };
}

// Defaults are left out so a plain link keeps following the latest two weeks.
export function buildReportSearchParams({ dateRange, filterModel, sortModel, paginationModel }: ReportState) {
  const params = new URLSearchParams();
  const [defaultStart, defaultEnd] = getDefaultDateRange();
  const [start, end] = dateRange;

  if (!start.isSame(defaultStart, 'day') || !end.isSame(defaultEnd, 'day')) {
    params.set('from', start.format(DATE_FORMAT));
    params.set('to', end.format(DATE_FORMAT));
  }
  const propertyName = getPropertyNameFilter(filterModel);
  if (propertyName) {
    params.set(PROPERTY_NAME_PARAM, propertyName);
  }
  (Object.keys(COUNTRY_PARAMS) as Array<CountryFilterField>).forEach((field) => {
    const codes = getCountryFilter(filterModel, field);
    if (codes.length > 0) {
      params.set(COUNTRY_PARAMS[field], codes.join(','));
    }
  });
  const [sort] = sortModel;
  const [defaultSort] = DEFAULT_SORT_MODEL;
  if (sort && (sort.field !== defaultSort.field || sort.sort !== defaultSort.sort)) {
    params.set('sortBy', sort.field);
    params.set('sortOrder', sort.sort ?? 'desc');
  }
  if (paginationModel.page > 0) {
    params.set('page', String(paginationModel.page));
  }
  if (paginationModel.pageSize !== PAGE_SIZE_OPTIONS[1]) {
    params.set('pageSize', String(paginationModel.pageSize));
  }
  return params;
}
